import "../css/ClusterListings.css";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Grid,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";
import {
  useActionWithPath,
  useClusterAllocationCheck,
  useProvisionableSkus,
} from "utils/query";
import { MAX_CLUSTER_NAME_SIZE } from "constants/common";
import {
  EnvironmentType,
  environmentTypeOptions,
  VpcInfoResponse,
} from "utils/saastypes";
import {
  BOOTSTRAP_PATH,
  HostedStatus,
  useHostedOptions,
} from "./OrkesHosted/api";

import LogoSelector from "components/LogoSelector";
import StyledMainButton from "components/buttons/StyledMainButton";
import HostedRegionSelector from "./OrkesHosted/HostedRegionSelector";
import awsLogo from "images/aws-logo.svg";
import azureLogo from "images/azure-logo.svg";
import gcloudLogo from "images/gcloud-logo.svg";

const CUSTOMER_UNAVAILABLE_SKUS = new Set([
  "SKU_8_OCU",
  "SKU_16_OCU",
  "SKU_24_OCU",
  "SKU_32_OCU",
]);

const POLL_INTERVAL_MS = 5_000;
const PREPARATION_TIMEOUT_MS = 25 * 60_000;

type Draft = {
  clusterName: string;
  region: string;
  skuName: string;
  environmentType: EnvironmentType;
  ingressType: "EXTERNAL" | "INTERNAL";
  creationRequested?: boolean;
};
type HostedVpc = Omit<VpcInfoResponse, "route53"> & {
  route53: { domainName: string; fixedDomain: boolean }[];
};

function savedDraft(key: string): Draft | undefined {
  try {
    const draft = JSON.parse(sessionStorage.getItem(key) ?? "null");
    return draft?.clusterName && draft?.region && draft?.skuName
      ? draft
      : undefined;
  } catch {
    return undefined;
  }
}

async function errorMessage(error: any): Promise<string> {
  try {
    const body = await error.json();
    if (typeof body?.message === "string") return body.message;
  } catch {
    /* A lost response is not proof that the operation failed. */
  }
  return error instanceof Error
    ? error.message
    : "The request could not be confirmed. Resume creation using the same cluster details.";
}

export default function CreateNewEnvironmentHostedOcuCluster() {
  const { authService } = useAuth();
  const [
    {
      isOrkesUser,
      canManageEnvironment,
      currentOrganization,
      currentUser,
      currentProfile,
      isSwitchingOrganization,
    },
  ] = useAuthMachine(authService);
  if (isSwitchingOrganization)
    return <CircularProgress aria-label="Switching organization" />;
  if (!canManageEnvironment || !currentOrganization) {
    return (
      <Alert severity="info">
        You do not have permission to create clusters in this organization.
      </Alert>
    );
  }
  // Switching organization/profile unmounts in-flight work and clears its displayed resources.
  return (
    <HostedForm
      key={`${currentUser?.uid}:${currentOrganization.id}:${currentProfile}:${isOrkesUser}`}
      storageKey={`orkes-hosted:${currentUser?.uid}:${currentOrganization.id}`}
      organizationName={currentOrganization.name}
      isOrkesUser={isOrkesUser}
    />
  );
}

function HostedForm({
  storageKey,
  organizationName,
  isOrkesUser,
}: {
  storageKey: string;
  organizationName: string;
  isOrkesUser: boolean;
}) {
  const options = useHostedOptions();
  const enabled =
    options.data?.enabled === true &&
    Array.isArray(options.data?.regions) &&
    options.data.regions.length > 0;
  const {
    data: skus,
    isFetching: loadingSkus,
    isError: skuError,
  } = useProvisionableSkus(enabled);
  const action = useActionWithPath({});
  const navigate = useNavigate();
  const live = useRef(true);
  const busy = useRef(false);
  const cancelWait = useRef<() => void>();
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      cancelWait.current?.();
    };
  }, []);

  const [draft, setDraft] = useState(() => savedDraft(storageKey));
  const [name, setName] = useState(draft?.clusterName ?? "");
  const [skuName, setSkuName] = useState(draft?.skuName ?? "");
  const [selectedRegion, setRegion] = useState(draft?.region ?? "");
  const [environmentType, setEnvironmentType] = useState<EnvironmentType>(
    draft?.environmentType === "ORKESTEST" && !isOrkesUser
      ? "TEST"
      : draft?.environmentType ?? "TEST"
  );
  const [ingressType, setIngressType] = useState<"EXTERNAL" | "INTERNAL">(
    draft?.ingressType ?? "EXTERNAL"
  );
  const [status, setStatus] = useState<HostedStatus>();
  const [domain, setDomain] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const sku = skus?.find((item) => item.skuName === skuName);
  const allocation = useClusterAllocationCheck().data;
  const gated = !!allocation?.checked;
  const onAws = allocation?.allowed.AWS ?? {};
  // Each section narrows the next, in the form's order: cloud, size, then environment.
  const cloudOffered = (cloud: string) =>
    !gated || !!allocation!.allowed[cloud.toUpperCase()];
  const sizeOffered = (skuName: string) =>
    !gated || Object.values(onAws).some((skus) => skus.includes(skuName));
  const environmentOffered = (environment: string) =>
    !gated ||
    (onAws[environment] ?? []).some((name) => !sku || name === sku.skuName);
  const visibleEnvironments = environmentTypeOptions.filter(
    (item) => isOrkesUser || item.id !== "ORKESTEST"
  );
  const offeredEnvironments = visibleEnvironments.filter((item) =>
    environmentOffered(item.id)
  );
  const choiceFits = !sku || environmentOffered(environmentType);
  const allowedSizes = Array.from(new Set(Object.values(onAws).flat()))
    .map((name) => Number(name.split("_")[1]))
    .sort((a, b) => a - b)
    .map((ocu) => `${ocu} OCU`);
  const showAllows = gated && !allocation!.message;
  // The size and environment lines are about AWS; say nothing there when AWS has none.
  const showAwsAllows = showAllows && Object.keys(onAws).length > 0;
  const firstOfferedEnvironment = offeredEnvironments[0]?.id;
  useEffect(() => {
    // Default to an allowed environment rather than leave the form on one it can't submit.
    if (
      !draft &&
      firstOfferedEnvironment &&
      !environmentOffered(environmentType)
    ) {
      setEnvironmentType(firstOfferedEnvironment as EnvironmentType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, firstOfferedEnvironment, environmentType, sku?.skuName]);
  const skuUnavailable = !isOrkesUser && CUSTOMER_UNAVAILABLE_SKUS.has(skuName);
  const validName =
    name.trim().length <= MAX_CLUSTER_NAME_SIZE &&
    /^[a-z0-9][a-z0-9-]*$/.test(name.trim().toLowerCase()) &&
    !name.toLowerCase().includes("-deleted-");
  const terminal =
    status?.state === "FAILED" || status?.state === "RECONCILIATION_REQUIRED";
  const canCreate =
    enabled &&
    !options.isError &&
    !!sku &&
    choiceFits &&
    !skuUnavailable &&
    !loadingSkus &&
    !skuError &&
    validName &&
    options.data!.regions.includes(selectedRegion) &&
    !pending &&
    !terminal &&
    !draft?.creationRequested;
  const setupComplete =
    status?.state === "STACK_READY" || !!draft?.creationRequested;
  const activeStep = setupComplete ? 1 : 0;

  function retain(next: Draft) {
    // Store only form inputs, scoped to this user and organization. Never credentials.
    // Write before any mutation so refresh cannot silently replace an uncertain attempt.
    sessionStorage.setItem(storageKey, JSON.stringify(next));
    setDraft(next);
  }

  async function request<T>(
    path: string,
    body?: unknown,
    method = "post"
  ): Promise<T> {
    if (!live.current)
      throw new Error(
        "The active organization changed. Reopen the Hosted form."
      );
    const result = await action.mutateAsync({
      method,
      path,
      body: JSON.stringify(body),
    } as never);
    if (!live.current)
      throw new Error(
        "The active organization changed. Reopen the Hosted form."
      );
    return result as T;
  }

  async function waitForNextCheck() {
    await new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        cancelWait.current = undefined;
        resolve();
      }, POLL_INTERVAL_MS);
      cancelWait.current = () => {
        window.clearTimeout(timer);
        cancelWait.current = undefined;
        resolve();
      };
    });
  }

  async function create() {
    if (busy.current || !canCreate) return;
    busy.current = true;
    setPending(true);
    setError("");
    let awaitingPreparation = false;
    let preparationConfirmed = !!status;
    try {
      // Older drafts may contain AZs; only retain current form inputs. Placement
      // is resolved by the backend immediately before provisioning.
      const input: Draft = {
        clusterName: draft?.clusterName ?? name.trim().toLowerCase(),
        region: selectedRegion,
        skuName,
        environmentType,
        ingressType,
      };
      if (!draft) {
        const exists = await request<boolean>(
          `/clusterNameExists?clusterName=${encodeURIComponent(
            input.clusterName
          )}`,
          undefined,
          "get"
        );
        if (exists !== false)
          throw new Error(
            "Choose a new cluster name. This name could not be confirmed as available."
          );
      }
      retain(input);
      setName(input.clusterName);
      const deadline = Date.now() + PREPARATION_TIMEOUT_MS;
      let result: HostedStatus;
      let previous: HostedStatus | undefined;
      while (true) {
        if (Date.now() >= deadline)
          throw new Error(
            "Preparation is taking longer than expected. Resume creation to check the same resources."
          );
        awaitingPreparation = true;
        result = await request<HostedStatus>(BOOTSTRAP_PATH, {
          clusterName: input.clusterName,
          region: input.region,
          skuName: input.skuName,
        });
        awaitingPreparation = false;
        preparationConfirmed = true;
        if (
          !result?.operationId ||
          result.clusterName !== input.clusterName ||
          result.region !== input.region ||
          (previous &&
            (result.operationId !== previous.operationId ||
              result.accountId !== previous.accountId))
        ) {
          throw new Error(
            "The preparation response did not match this cluster. Contact an operator before continuing."
          );
        }
        setStatus(result);
        if (
          result.state === "FAILED" ||
          result.state === "RECONCILIATION_REQUIRED"
        )
          return;
        if (result.state === "STACK_READY") break;
        if (result.state !== "PLANNED" && result.state !== "CREATING")
          throw new Error(
            "Unexpected preparation status. Contact an operator before continuing."
          );
        previous = result;
        await waitForNextCheck();
        if (!live.current) return;
      }
      if (!result.vpcId || !result.vpcCidr || !result.accountId)
        throw new Error("Prepared network details are incomplete.");
      const network = await request<HostedVpc>("/vpcInfo", {
        clusterName: result.clusterName,
        customerAccountId: result.accountId,
        region: result.region,
        vpcId: result.vpcId,
      });
      if (network?.vpcCIDR !== result.vpcCidr)
        throw new Error("The prepared network could not be verified.");
      if (network.vpcSubnets?.length)
        throw new Error(
          "The prepared network is already in use. Check the cluster status before continuing."
        );
      const fixedDomain = network.route53?.find(
        (zone) => zone.fixedDomain
      )?.domainName;
      if (!fixedDomain)
        throw new Error(
          "No Orkes domain is available for this cluster. Contact an operator."
        );
      setDomain(fixedDomain);
      const body = {
        clusterName: result.clusterName,
        cloudProvider: "AWS",
        clusterType: "ENTERPRISE",
        customerAccountId: result.accountId,
        region: result.region,
        vpcId: result.vpcId,
        vpcCidr: result.vpcCidr,
        skuName: input.skuName,
        environmentType: input.environmentType,
        ingressType: input.ingressType,
        domainUrl: fixedDomain,
        conductorUIURL: fixedDomain,
        rdsEnabled: true,
        enableAuthentication: true,
      };
      // Existing validation, including capacity/quota checks, remains authoritative.
      await request("/provisionEnvironmentDryRun", body);
      retain({ ...input, creationRequested: true });
      const cluster = await request<{ name: string }>(
        "/provisionEnvironment",
        body
      );
      if (cluster?.name !== input.clusterName)
        throw new Error("Cluster creation could not be confirmed.");
      sessionStorage.removeItem(storageKey);
      navigate(
        `/home/clusterprogess?clusterName=${encodeURIComponent(
          cluster.name
        )}&showSucess=true`
      );
    } catch (failure) {
      const message = await errorMessage(failure);
      if (live.current) {
        if (
          awaitingPreparation &&
          !preparationConfirmed &&
          (failure as { status?: number })?.status === 400 &&
          message ===
            "A cluster or CFT identity already exists in the history of this cluster name"
        ) {
          // Planning rejects this name before saving an operation or creating resources.
          // Other failures may have started preparation, so retain their retry identity.
          sessionStorage.removeItem(storageKey);
          setDraft(undefined);
          setError(
            "This cluster name has been used before. Choose a different name."
          );
        } else {
          setError(message);
        }
      }
    } finally {
      busy.current = false;
      if (live.current) setPending(false);
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Helmet>
        <title>Orkes Cloud - Create Orkes Hosted Cluster</title>
      </Helmet>
      <Paper
        sx={(theme) => ({
          mt: "29px",
          mx: { xs: 2, md: "30px" },
          p: "20px",
          maxWidth: 900,
          // Keep the immutable prepared details readable during the final review.
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "& .MuiInputLabel-root.Mui-disabled, & .MuiFormHelperText-root.Mui-disabled":
            { color: theme.palette.text.secondary },
        })}
      >
        <Typography variant="h2" sx={{ mb: 1 }}>
          Create Orkes Hosted Cluster
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Organization: {organizationName}. Dedicated AWS infrastructure managed
          by Orkes.
        </Typography>
        {options.isLoading && (
          <CircularProgress aria-label="Loading Hosted availability" />
        )}
        {!options.isLoading && (!enabled || options.isError) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Orkes Hosted creation is not available for this session. An
            authorized operator must enable a region and network pool first.
          </Alert>
        )}
        <Stack spacing={3}>
          <Typography variant="h6">Cloud provider</Typography>
          {gated && allocation!.message && (
            <Alert severity="warning">{allocation!.message}</Alert>
          )}
          {showAllows && (
            <Alert severity="info">
              {`Your subscription allows: ${Object.keys(allocation!.allowed)
                .map((cloud) =>
                  cloud === "AWS" || cloud === "GCP" ? cloud : "Azure"
                )
                .join(", ")}`}
            </Alert>
          )}
          <Box
            role="group"
            aria-label="Cloud provider"
            sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}
          >
            {[
              { name: "AWS", logo: awsLogo, available: true },
              { name: "Azure", logo: azureLogo, available: false },
              { name: "GCP", logo: gcloudLogo, available: false },
            ].map((provider) => (
              <ButtonBase
                key={provider.name}
                aria-label={provider.name}
                aria-pressed={provider.available}
                disabled={
                  !provider.available ||
                  !cloudOffered(provider.name) ||
                  !enabled ||
                  !!draft ||
                  pending
                }
                sx={{
                  borderRadius: "20px",
                  opacity:
                    provider.available && cloudOffered(provider.name) ? 1 : 0.4,
                }}
              >
                <LogoSelector
                  src={provider.logo}
                  isActive={provider.available}
                  isEnabled={provider.available}
                />
              </ButtonBase>
            ))}
          </Box>

          {showAwsAllows && (
            <Alert severity="info">{`Your subscription allows: ${allowedSizes.join(
              ", "
            )}`}</Alert>
          )}
          <TextField
            select
            size="small"
            sx={{ maxWidth: 400 }}
            label="OCU capacity"
            value={sku ? skuName : ""}
            disabled={
              !enabled || pending || !!draft?.creationRequested || terminal
            }
            onChange={(event) => {
              setSkuName(event.target.value);
            }}
            helperText={
              loadingSkus
                ? "Loading OCU sizes..."
                : "Choose the capacity for your new cluster."
            }
          >
            {(skus ?? []).map((item) => (
              <MenuItem
                key={item.skuName}
                value={item.skuName}
                disabled={
                  !sizeOffered(item.skuName) ||
                  (!isOrkesUser && CUSTOMER_UNAVAILABLE_SKUS.has(item.skuName))
                }
              >
                {item.title}
              </MenuItem>
            ))}
          </TextField>
          <HostedRegionSelector
            availableRegions={options.data?.regions ?? []}
            selectedRegion={selectedRegion}
            disabled={!enabled || options.isError || !!draft || pending}
            onSelect={setRegion}
          />
          {enabled &&
            !draft?.creationRequested &&
            !loadingSkus &&
            (skuError || !skus?.length) && (
              <Alert severity="error">
                OCU sizes could not be loaded. Reload this page or contact an
                operator.
              </Alert>
            )}
          {draft &&
            !draft.creationRequested &&
            !loadingSkus &&
            !skuError &&
            !!skus?.length &&
            (!sku || skuUnavailable) && (
              <Alert severity="warning">
                The saved OCU size is no longer available. Choose another OCU
                size to resume preparation.
              </Alert>
            )}
          <Typography variant="h6">Cluster properties</Typography>
          <Grid container spacing={2} sx={{ maxWidth: 700 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Cluster name"
                value={name}
                disabled={!!draft || pending}
                onChange={(event) => setName(event.target.value)}
                inputProps={{ maxLength: MAX_CLUSTER_NAME_SIZE }}
                error={!!name && !validName}
                helperText={`Up to ${MAX_CLUSTER_NAME_SIZE} letters, numbers and hyphens. Start with a letter or number.`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {showAwsAllows && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {`Your subscription allows: ${offeredEnvironments
                    .map((item) => item.title)
                    .join(", ")}`}
                </Alert>
              )}
              <TextField
                size="small"
                fullWidth
                select
                label="Environment"
                value={environmentType}
                disabled={!!draft || pending}
                onChange={(event) =>
                  setEnvironmentType(event.target.value as EnvironmentType)
                }
                error={!choiceFits}
                helperText={
                  choiceFits
                    ? undefined
                    : "Your subscription does not cover this size in this environment."
                }
              >
                {visibleEnvironments.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    disabled={!environmentOffered(item.id)}
                  >
                    {item.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                select
                label="Connectivity"
                value={ingressType}
                disabled={!!draft || pending}
                onChange={(event) =>
                  setIngressType(event.target.value as "EXTERNAL" | "INTERNAL")
                }
              >
                <MenuItem value="EXTERNAL">
                  Internet access with authentication
                </MenuItem>
                <MenuItem value="INTERNAL">
                  Private access (VPN or peering required)
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {error && <Alert severity="error">{error}</Alert>}
          {draft && (
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              aria-label="Cluster creation progress"
            >
              {[
                setupComplete ? "Setup complete" : "Setting up resources",
                "Creating your cluster",
              ].map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel error={!!error && activeStep === index}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          {draft?.creationRequested ? (
            <Alert severity="warning">
              Your cluster creation request has been sent. Check the cluster
              status before taking further action. This form will not submit it
              again.
            </Alert>
          ) : terminal ? (
            <Alert severity="warning">
              Preparation needs operator review. Resources remain reserved for
              this cluster; contact an operator before starting another attempt.
            </Alert>
          ) : draft ? (
            <Alert severity="info" role="status">
              {pending
                ? setupComplete
                  ? "Setup complete. Creating your cluster..."
                  : "We're preparing the resources needed for your cluster."
                : "Creation is paused. Resume with the same cluster details to continue."}
            </Alert>
          ) : null}
          <Typography variant="body2">
            {domain
              ? `Your cluster will use an Orkes address under ${domain}. `
              : "Your cluster will use an Orkes-managed address. "}
            Keep this page open until cluster creation starts. If you leave,
            return to resume setup.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {!draft?.creationRequested && !terminal && (
              <StyledMainButton
                sx={{ width: 250 }}
                disabled={!canCreate}
                onClick={create}
              >
                {pending
                  ? "Creating..."
                  : draft
                  ? "Resume creation"
                  : "Create cluster"}
              </StyledMainButton>
            )}
            {draft?.creationRequested && (
              <Button
                component={Link}
                to={`/home/clusterprogess?clusterName=${encodeURIComponent(
                  draft.clusterName
                )}`}
              >
                View cluster status
              </Button>
            )}
            <Button component={Link} to="/home/clusters">
              Back to clusters
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
